/**
 * components/SessionTimeoutModal.tsx
 * ------------------------------------------------------------------
 * AUDIT M8 — the session-timeout WARNING surface. The timing logic
 * already lived in AuthContext (sessionExpiring flag, extendSession,
 * logout); this is purely the missing UI. When the inactivity
 * warning window opens, this modal appears with a live countdown
 * and lets the user stay signed in or sign out now.
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import { Clock, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { warningFor } from '@/lib/sessionIdle';

// Counts down to AuthContext's `sessionExpiresAt`, which is shared by every
// tab (lib/sessionIdle.ts) — so activity in another tab moves it too. It
// counts to a MOMENT rather than running its own clock, because a phone or a
// sleeping laptop pauses timers and the countdown would then lie.
export default function SessionTimeoutModal() {
  const { sessionExpiring, sessionExpiresAt, idleLimitMs, extendSession, logout } = useAuth();
  const [remaining, setRemaining] = useState(warningFor(idleLimitMs));

  useEffect(() => {
    if (!sessionExpiring || !sessionExpiresAt) return undefined;
    const tick = () => setRemaining(Math.max(0, sessionExpiresAt - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [sessionExpiring, sessionExpiresAt]);

  if (!sessionExpiring) return null;

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  return (
    <div className="fixed inset-0 z-[110] bg-[#0e1830]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#1b3a6b] to-[#d4a92c] px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-xl ring-1 ring-white/30 flex items-center justify-center">
              <Clock size={22} />
            </div>
            <div>
              <p className="font-bold text-lg">Still there?</p>
              <p className="text-white/70 text-xs">
                You'll be signed out for inactivity
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            For security, this session will end in
          </p>
          <p className="text-3xl font-bold text-primary my-2 tabular-nums">
            {mins}:{secs.toString().padStart(2, '0')}
          </p>
          <p className="text-xs text-gray-500">
            Choose to stay signed in to continue working, or sign
            out now.
          </p>
          <div className="flex gap-2 mt-5">
            <button
              onClick={extendSession}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#2d5295] to-[#1b3a6b] text-white font-semibold text-sm hover:scale-[1.02] transition-transform"
            >
              <ShieldCheck size={16} />
              Stay signed in
            </button>
            <button
              onClick={logout}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
